<template>
  <div class="container-fluid">
    <div class="d-sm-flex align-items-center justify-content-between mb-4">
      <h1 class="h3 mb-0 text-gray-800">{{ isEditMode ? 'Edit Artist' : 'Add Artist' }}</h1>
      <button class="btn btn-secondary" @click="goBack">
        <i class="fas fa-arrow-left"></i> Back to Artists
      </button>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center my-5">
      <div class="spinner-border text-primary" role="status">
        <span class="sr-only">Loading...</span>
      </div>
    </div>

    <!-- Form -->
    <div v-else class="card shadow mb-4">
      <div class="card-header py-3">
        <h6 class="m-0 font-weight-bold text-primary">Artist Details</h6>
      </div>
      <div class="card-body">
        <form @submit.prevent="saveArtist">
          <div class="row">
            <div class="col-md-8">
              <div class="form-group">
                <label for="artistName">Display Name *</label>
                <input v-model="formData.name" id="artistName" class="form-control" type="text" placeholder="Display Name" required />
              </div>
            </div>
            <div class="col-md-4">
              <div class="form-group">
                <label for="artistSortOrder">Sort Order</label>
                <input v-model.number="formData.sortOrder" id="artistSortOrder" class="form-control" type="number" min="0" placeholder="0" />
              </div>
            </div>
          </div>
          
          <div class="form-group">
            <label for="artistSubtitle">Subtitle</label>
            <input v-model="formData.subtitle" id="artistSubtitle" class="form-control" type="text" placeholder="Subtitle (e.g., Hair Stylist)" />
          </div>
          
          <div class="form-group">
            <label for="artistDescription">Description</label>
            <input v-model="formData.description" id="artistDescription" class="form-control" type="text" placeholder="Short description" />
          </div>
          
          <div class="row">
            <div class="col-md-8">
              <div class="form-group">
                <label for="artistImage">Artist Image</label>
                <div class="custom-file">
                  <input 
                    type="file" 
                    class="custom-file-input" 
                    id="artistImage" 
                    @change="handleFileSelect"
                    accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                  />
                  <label class="custom-file-label" for="artistImage">
                    {{ selectedFileName || 'Choose file...' }}
                  </label>
                </div>
                <small class="form-text text-muted">Accepted formats: JPEG, PNG, GIF, WebP (max 5MB)</small>
                <!-- Current image preview -->
                <div v-if="formData.imagePath && !imagePreview" class="mt-2">
                  <small class="text-muted d-block mb-2">Current image:</small>
                  <img :src="getImageUrl(formData.imagePath)" alt="Current image" class="img-thumbnail" style="max-height: 150px;" />
                </div>
                <!-- New image preview -->
                <div v-if="imagePreview" class="mt-2">
                  <small class="text-muted d-block mb-2">New image preview:</small>
                  <img :src="imagePreview" alt="Preview" class="img-thumbnail" style="max-height: 150px;" />
                  <button type="button" class="btn btn-sm btn-outline-danger ml-2" @click="clearImageSelection">
                    <i class="fas fa-times"></i> Remove
                  </button>
                </div>
              </div>
            </div>
            <div class="col-md-4">
              <div class="form-group">
                <label for="artistImageAlt">Image Alt Text</label>
                <input v-model="formData.imageAlt" id="artistImageAlt" class="form-control" type="text" placeholder="Alt text for image" />
              </div>
            </div>
          </div>
          
          <div class="form-group">
            <label>Bio</label>
            <Editor
              v-model="formData.bio" 
              :api-key="tinymceApiKey" 
              :init="{
                height: 350,
                menubar: false,
                plugins: 'link code',
                toolbar: 'undo redo | bold italic forecolor | alignleft aligncenter alignright alignjustify | removeformat | link code',
                color_map_foreground: [
                  '000000', 'Black',
                  'FFFFFF', 'White',
                  'e2b06e', 'Gold',
                ],
                content_style: 'body { background-color: #878e95; }',
              }"
            />
          </div>
          
          <div class="form-group">
            <div class="custom-control custom-switch">
              <input type="checkbox" class="custom-control-input" id="artistEnabled" v-model="formData.enabled">
              <label class="custom-control-label" for="artistEnabled">Enabled (visible on website)</label>
            </div>
          </div>
          
          <hr />
          
          <div class="d-flex justify-content-end">
            <button type="button" class="btn btn-secondary mr-2" @click="goBack">
              Cancel
            </button>
            <button type="submit" class="btn btn-primary" :disabled="saving">
              <span v-if="saving" class="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true"></span>
              {{ saving ? 'Saving...' : 'Save Artist' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Toast notifications -->
    <div class="position-fixed bottom-0 right-0 p-3" style="z-index: 9999; right: 0; bottom: 0;">
      <div v-if="toast.show" class="toast show" role="alert" aria-live="assertive" aria-atomic="true">
        <div class="toast-header" :class="toast.type === 'success' ? 'bg-success text-white' : 'bg-danger text-white'">
          <strong class="mr-auto">{{ toast.type === 'success' ? 'Success' : 'Error' }}</strong>
          <button type="button" class="ml-2 mb-1 close text-white" @click="toast.show = false">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <div class="toast-body">
          {{ toast.message }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Editor from '@tinymce/tinymce-vue';
import fetchWrapper from '@/utils/fetchWrapper';

export default {
  name: 'AdminArtistForm',
  components: {
    Editor
  },
  props: {
    id: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      loading: false,
      saving: false,
      selectedFile: null,
      selectedFileName: '',
      imagePreview: null,
      tinymceApiKey: process.env.VUE_APP_TINYMCE_API_KEY,
      formData: {
        name: '',
        subtitle: '',
        description: '',
        bio: '',
        imagePath: '',
        imageAlt: '',
        enabled: true,
        sortOrder: 0
      },
      toast: {
        show: false,
        type: 'success',
        message: ''
      }
    };
  },
  computed: {
    isEditMode() {
      return !!this.id;
    }
  },
  created() {
    if (this.isEditMode) {
      this.fetchArtist();
    } else {
      this.fetchNextSortOrder();
    }
  },
  methods: {
    async fetchArtist() {
      this.loading = true;
      try {
        const response = await fetchWrapper(`/api/artists/${this.id}`);
        const data = await response.json();
        
        if (data.status === 'success' && data.artist) {
          this.formData = {
            name: data.artist.name || '',
            subtitle: data.artist.subtitle || '',
            description: data.artist.description || '',
            bio: data.artist.bio || '',
            imagePath: data.artist.imagePath || '',
            imageAlt: data.artist.imageAlt || '',
            enabled: data.artist.enabled,
            sortOrder: data.artist.sortOrder || 0
          };
        } else {
          this.showToast('error', 'Artist not found');
          this.$router.push('/admin/pages_about');
        }
      } catch (error) {
        console.error('Error fetching artist:', error);
        this.showToast('error', 'Failed to load artist');
      } finally {
        this.loading = false;
      }
    },
    
    async fetchNextSortOrder() {
      try {
        const response = await fetchWrapper('/api/artists');
        const data = await response.json();
        
        if (data.status === 'success' && data.artists && data.artists.length > 0) {
          this.formData.sortOrder = Math.max(...data.artists.map(a => a.sortOrder)) + 1;
        }
      } catch (error) {
        console.error('Error fetching artists for sort order:', error);
      }
    },
    
    handleFileSelect(event) {
      const file = event.target.files[0];
      if (file) {
        // Validate file size (5MB)
        if (file.size > 5 * 1024 * 1024) {
          this.showToast('error', 'File size must be less than 5MB');
          event.target.value = '';
          return;
        }
        
        // Validate file type
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
          this.showToast('error', 'Only image files (JPEG, PNG, GIF, WebP) are allowed');
          event.target.value = '';
          return;
        }
        
        this.selectedFile = file;
        this.selectedFileName = file.name;
        
        // Create preview
        const reader = new FileReader();
        reader.onload = (e) => {
          this.imagePreview = e.target.result;
        };
        reader.readAsDataURL(file);
      }
    },
    
    clearImageSelection() {
      this.selectedFile = null;
      this.selectedFileName = '';
      this.imagePreview = null;
      const fileInput = document.getElementById('artistImage');
      if (fileInput) fileInput.value = '';
    },
    
    getImageUrl(imagePath) {
      if (!imagePath) return null;
      if (imagePath.startsWith('artists/')) {
        return `/images/${imagePath}`;
      }
      return `/images/${imagePath}`;
    },
    
    async saveArtist() {
      if (!this.formData.name.trim()) {
        this.showToast('error', 'Name is required');
        return;
      }
      
      this.saving = true;
      try {
        const url = this.isEditMode ? `/api/artists/${this.id}` : '/api/artists';
        const method = this.isEditMode ? 'PUT' : 'POST';
        
        // Use FormData for file upload
        const formData = new FormData();
        formData.append('name', this.formData.name);
        formData.append('subtitle', this.formData.subtitle);
        formData.append('description', this.formData.description);
        formData.append('bio', this.formData.bio);
        formData.append('imageAlt', this.formData.imageAlt);
        formData.append('enabled', this.formData.enabled);
        formData.append('sortOrder', this.formData.sortOrder);
        
        // Append file if selected
        if (this.selectedFile) {
          formData.append('image', this.selectedFile);
        }
        
        const response = await fetchWrapper(url, {
          method,
          body: formData
        });
        
        const data = await response.json();
        
        if (data.status === 'success') {
          this.showToast('success', this.isEditMode ? 'Artist updated successfully' : 'Artist created successfully');
          // Navigate back after a short delay
          setTimeout(() => {
            this.$router.push('/admin/pages_about');
          }, 1000);
        } else {
          this.showToast('error', data.message || 'Failed to save artist');
        }
      } catch (error) {
        console.error('Error saving artist:', error);
        this.showToast('error', 'Failed to save artist');
      } finally {
        this.saving = false;
      }
    },
    
    goBack() {
      this.$router.push('/admin/pages_about');
    },
    
    showToast(type, message) {
      this.toast = { show: true, type, message };
      setTimeout(() => {
        this.toast.show = false;
      }, 5000);
    }
  }
}
</script>

<style scoped>
.toast {
  min-width: 250px;
}
</style>
